import CheckIcon from "@mui/icons-material/Check";
import * as MUI from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { Fragment } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as FcIcons from "react-icons/fc";
import * as FiIcons from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { LoadingContext } from "../../../contexts/Loading";
import { STATUSES } from "../../../enums/AppointmentStatus";
import {
  ACCEPT_PENDING,
  DELETE_APPOINTMENT,
  EDIT_APPOINTMENT,
  REJECT_PENDING,
} from "../../../enums/PermissionTitles";
import * as keys from "../../../enums/QueryKeys";
import { deleteAppointment } from "../../../enums/queryFunction/Delete";
import useConfirm from "../../../hooks/useConfirm";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser, { useGetUserId } from "../../../hooks/useUser";
import PendingConfirmationTemplate from "../../admin/pendingAppointments/components/PendingConfirmationTemplate";
import usePendingStatus from "../../admin/pendingAppointments/hooks/usePendingStatus";
export default function DeleteAndEditAppointment({
  handleEditing,
  appointment,
  handleSnackbar,
}) {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const currentAppointmentKey = keys.APPOINTMENT_GENERAL + id;
  const userId = useGetUserId();
  const { roles } = useUser();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const navigate = useNavigate();
  const currentAppointment = appointment;
  const { isLoading, setIsLoading } = React.useContext(LoadingContext);
  const isUserAdmin = roles.adminOrEmployee;
  const color = usePrimaryColor();
  const { t } = useTranslation();
  const put = usePendingStatus(appointment?.id);
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning"
  );
  const deleteRenderCondition = getDeleteCondition();

  function getDeleteCondition() {
    if (roles.master) {
      return true;
    } else if (roles.adminOrEmployee) {
      return currentAppointment?.status != STATUSES.INACTIVE;
    }
    return (
      currentAppointment?.status == STATUSES.WAITING_CLINIC ||
      currentAppointment?.status == STATUSES.WAITING_USER
    );
  }

  const cancelationCondition =
    isPermissionAllowed(REJECT_PENDING) &&
    (currentAppointment?.status == STATUSES.WAITING_CLINIC ||
      currentAppointment?.status == STATUSES.WAITING_USER);

  const isAppointmentDone =
    currentAppointment?.status === STATUSES.FINISHED ||
    currentAppointment?.status === STATUSES.RESOLVED;

  const methods = useForm({
    defaultValues: {
      statusMessage:
        t("Dear") +
        " " +
        currentAppointment?.user?.fullName +
        ", " +
        t("RejectDefaultMessage"),
    },
  });

  //Add use mutate here to remove the appointment from a list and don't refetch data
  async function handleDeleteAppointement() {
    getMessage(
      isUserAdmin
        ? t("DeletingAppointmentAdmin")
        : t("CancelingAnAppointmentUser")
    );
    const ans = await confirm();
    if (ans) {
      setIsLoading(true);
      const appointment = currentAppointment;
      deleteMutate(appointment?.id);
    }
  }

  function removeAppointent(key) {
    queryClient.setQueryData([key], (oldData) => {
      if (!oldData) return oldData;
      return oldData.filter((item) => item.id !== currentAppointment.id);
    });
  }

  function removeFromPetsAppointments(petId) {
    queryClient.setQueryData([keys.PETS_APPOINTMENTS + petId], (oldData) => {
      if (!oldData) return oldData;
      const filterList = oldData.appointmentList.filter(
        (item) => item.id !== currentAppointment.id
      );
      const updatedObject = {
        speciesAllowed: oldData.speciesAllowed,
        appointmentList: filterList,
      };
      return updatedObject;
    });
  }

  //A user deleting an appointment
  const { mutate: deleteMutate } = useMutation({
    mutationFn: () => deleteAppointment(appointment?.id, userId),
    onSuccess: () => {
      const petId = appointment?.pet?.id;
      const queries = [keys.APPOINTMENTS, keys.PENDING_APPOINTMENTS];
      queries.forEach((q) => removeAppointent(q));
      queryClient.invalidateQueries([currentAppointmentKey]);
      removeFromPetsAppointments(petId);
      handleSnackbar("successMessage", "success");
      setIsLoading(false);
      navigate(-1);
    },
    onError: (error) => {
      const backendError = error?.response?.data;
      handleSnackbar(backendError ? backendError : "errorMessage", "error");
      setIsLoading(false);
    },
  });

  //Method check the cancelation condition and either deletes or cancels the
  //appointment accordingly
  function handleCancelation(status) {
    if (cancelationCondition) {
      handleStatusAppointment(status);
    } else {
      handleDeleteAppointement();
    }
  }

  //Method accpets or rejects an appointment
  const handleStatusAppointment = async (acceptance_status) => {
    const appointment = currentAppointment;
    getMessage(
      <FormProvider {...methods}>
        <PendingConfirmationTemplate status={acceptance_status} />
      </FormProvider>
    );
    const ans = await confirm();
    if (ans) {
      mutate({
        status: acceptance_status,
        message: methods.getValues("statusMessage"),
      });
    }
  };

  //Accpeting and or rejecting an appointment
  const { mutate } = useMutation({
    mutationFn: ({ status, message }) => {
      if (status === STATUSES.ACTIVE) {
        return put.acceptFn(message);
      } else {
        return put.rejectFn(message);
      }
    },
    onSuccess: (response) => {
      const queries = [keys.PENDING_APPOINTMENTS];
      queries.forEach((q) => removeAppointent(q));
      queryClient.invalidateQueries(keys.APPOINTMENTS);
      updateAppoinmentView(response?.data);
      queryClient.setQueryData(
        [keys.APPOINTMENTS + currentAppointment?.id],
        response?.data
      );
      handleSnackbar("successMessage", "success");
    },
    onError: (error) => {
      const backendError = error?.response?.data;
      handleSnackbar(backendError ? backendError : "errorMessage", "error");
    },
  });

  //Method for updating the current appointment view query
  //Not usable by other keys
  function updateAppoinmentView(response) {
    if (response) {
      queryClient.setQueryData([currentAppointmentKey], (oldData) => {
        if (!oldData) {
          return oldData;
        }
        return {
          usersPets: oldData.usersPets,
          appointment: response,
        };
      });
    }
  }

  if (deleteRenderCondition) {
    return (
      <Fragment>
        <MUI.Tooltip
          title={
            !isPermissionAllowed(EDIT_APPOINTMENT)
              ? t("NoPermissionForThisAction")
              : t("EditAppointmnet")
          }
        >
          <MUI.IconButton
            disabled={ !isPermissionAllowed(EDIT_APPOINTMENT) || currentAppointment.pet?.deceased }
            onClick={() => handleEditing()}
            data-testid={"appointment.details.edit-button"}
          >
            <FiIcons.FiEdit />
          </MUI.IconButton>
        </MUI.Tooltip>

        {!isAppointmentDone ? (
          <MUI.Tooltip
            title={
              !isPermissionAllowed(REJECT_PENDING) &&
              !isPermissionAllowed(DELETE_APPOINTMENT)
                ? t("NoPermissionForThisAction")
                : t("RejectAppointmnet")
            }
          >
            <MUI.IconButton
              disabled={
                !isPermissionAllowed(REJECT_PENDING) &&
                !isPermissionAllowed(DELETE_APPOINTMENT)
              }
              onClick={() => handleCancelation(STATUSES.INACTIVE)}
              data-testid={"appointment.details.delete-button"}
            >
              <FcIcons.FcCancel color="red" />
            </MUI.IconButton>
          </MUI.Tooltip>
        ) : null}
        {cancelationCondition ? (
          <MUI.Tooltip
            title={
              !isPermissionAllowed(ACCEPT_PENDING)
                ? t("NoPermissionForThisAction")
                : t("AcceptAppointmnet")
            }
          >
            <MUI.IconButton
              disabled={!isPermissionAllowed(ACCEPT_PENDING)}
              onClick={() => handleStatusAppointment(STATUSES.ACTIVE)}
              data-testid={"appointment.details.accept-button"}
            >
              <CheckIcon sx={{ color: color }} />
            </MUI.IconButton>
          </MUI.Tooltip>
        ) : null}
        <ConfirmationDialog />
      </Fragment>
    );
  }
}
